import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import toast from "react-hot-toast";
import UserContext from "../../context/UserContext";
import { buildUserState } from "../../utilities/tokenUtils";

export default function DetailModal({ item, onClose, token }) {
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { setUserData } = useContext(UserContext);
  const navigate = useNavigate();

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      toast.dismiss();
      toast.error("Sesión expirada. Por favor inicie sesión nuevamente.");
      localStorage.removeItem("auth-token");
      sessionStorage.removeItem("partial-token");
      sessionStorage.removeItem("setup-token");
      setUserData(buildUserState(undefined));
      navigate("/admin/login");
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (item && item.id) {
      fetchDetails();
    }
  }, [item]);

  const fetchDetails = async () => {
    try {
      setIsLoading(true);
      const response = await Axios.get(
        `${process.env.REACT_APP_BACK_DOMAIN}/archive/item/${item.id}`,
        {
          headers: { "x-auth-token": token },
        }
      );
      setDetails(response.data);
    } catch (err) {
      console.error("Error fetching details:", err);
      if (!handleAuthError(err)) {
        toast.error("Error al cargar los detalles");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (dataId) => {
    try {
      toast.loading("Iniciando descarga...");
      
      const response = await Axios.get(
        `${process.env.REACT_APP_BACK_DOMAIN}/archive/download/${dataId}`,
        {
          headers: { "x-auth-token": token },
          responseType: "blob",
        }
      );

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Obtener nombre del archivo desde headers o inferir extensión del Content-Type
      const contentDisposition = response.headers["content-disposition"];
      let filename = null;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        const contentType = response.headers["content-type"] || "";
        const extMap = { "image/jpeg": ".jpg", "image/png": ".png", "image/gif": ".gif", "image/tiff": ".tiff", "image/bmp": ".bmp", "image/webp": ".webp" };
        const ext = extMap[contentType.split(";")[0]] || ".bin";
        filename = `archivo_${dataId}${ext}`;
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success("Descarga completada");
    } catch (err) {
      console.error("Error downloading file:", err);
      if (!handleAuthError(err)) {
        toast.dismiss();
        toast.error("Error al descargar el archivo");
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatXmlText = (xmlText) => {
    if (!xmlText) return "";
    
    try {
      const parser = new DOMParser();
      // Intentar parseo estricto XML
      const xmlDoc = parser.parseFromString(xmlText, "text/xml");
      
      // Detectar error de parseo XML (nodo parsererror)
      if (xmlDoc.querySelector("parsererror")) {
        // Fallback: parsear como HTML (permisivo)
        const htmlDoc = parser.parseFromString(xmlText, "text/html");
        const textContent = htmlDoc.body?.textContent || xmlText;
        return textContent;
      }
      
      const textContent = xmlDoc.documentElement?.textContent || xmlText;
      // Retorna el contenido completo, sin truncar, para que el usuario pueda decidir cuánto leer
      return textContent;
    } catch {
      // Último fallback: strip tags manualmente
      const stripped = xmlText.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      return stripped;
    }
  };

  if (!item) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{item.title || "Sin título"}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="modal-loading">
              <p>Cargando detalles...</p>
            </div>
          ) : details ? (
            <>
              {details.provenance?.warning && (
                <div className="provenance-warning" role="alert">
                  {details.provenance.warning}
                </div>
              )}
              {details.type === "image" && (
                <div className="detail-image">
                  {details.preview && (
                    <div className="detail-image__preview">
                      <img src={details.preview} alt={details.title} />
                    </div>
                  )}
                  
                  <div className="detail-metadata">
                    <h3>Información de la imagen</h3>
                    
                    <div className="metadata-grid">
                      <div className="metadata-item">
                        <span className="metadata-label">Dimensiones:</span>
                        <span className="metadata-value">
                          {details.imageData?.width} × {details.imageData?.height} px
                        </span>
                      </div>

                      {details.summary && (
                        <div className="metadata-item metadata-item--full">
                          <span className="metadata-label">Sumario:</span>
                          <span className="metadata-value">{details.summary}</span>
                        </div>
                      )}

                      {details.description && (
                        <div className="metadata-item metadata-item--full">
                          <span className="metadata-label">Descripción del registro:</span>
                          <span className="metadata-value">{details.description}</span>
                        </div>
                      )}

                      {details.imageData?.description && (
                        <div className="metadata-item metadata-item--full">
                          <span className="metadata-label">Descripción:</span>
                          <span className="metadata-value">{details.imageData.description}</span>
                        </div>
                      )}

                      {details.caption && details.caption !== details.summary && (
                        <div className="metadata-item metadata-item--full">
                          <span className="metadata-label">Pie de foto:</span>
                          <span className="metadata-value">{details.caption}</span>
                        </div>
                      )}

                      <div className="metadata-item metadata-item--full">
                        <span className="metadata-label">Keywords:</span>
                        <span className="metadata-value">
                          {details.keywords || "No hay keywords registradas para este elemento."}
                        </span>
                      </div>

                      {details.imageData?.city && (
                        <div className="metadata-item">
                          <span className="metadata-label">Ciudad:</span>
                          <span className="metadata-value">{details.imageData.city}</span>
                        </div>
                      )}

                      {details.imageData?.country && (
                        <div className="metadata-item">
                          <span className="metadata-label">País:</span>
                          <span className="metadata-value">{details.imageData.country}</span>
                        </div>
                      )}

                      {details.imageData?.credit && (
                        <div className="metadata-item">
                          <span className="metadata-label">Crédito:</span>
                          <span className="metadata-value">{details.imageData.credit}</span>
                        </div>
                      )}

                      {details.imageData?.location && (
                        <div className="metadata-item">
                          <span className="metadata-label">Ubicación:</span>
                          <span className="metadata-value">{details.imageData.location}</span>
                        </div>
                      )}

                      {details.imageData?.instructions && (
                        <div className="metadata-item metadata-item--full">
                          <span className="metadata-label">Instrucciones:</span>
                          <span className="metadata-value">{details.imageData.instructions}</span>
                        </div>
                      )}

                      {details.imageData?.authors && (
                        <div className="metadata-item">
                          <span className="metadata-label">Autor(es) / creador(es):</span>
                          <span className="metadata-value">{details.imageData.authors}</span>
                        </div>
                      )}

                      {details.imageData?.orgSource && (
                        <div className="metadata-item">
                          <span className="metadata-label">Agencia:</span>
                          <span className="metadata-value">{details.imageData.orgSource}</span>
                        </div>
                      )}

                      {details.imageData?.source && (
                        <div className="metadata-item">
                          <span className="metadata-label">Fuente:</span>
                          <span className="metadata-value">{details.imageData.source}</span>
                        </div>
                      )}

                      {details.imageData?.eventDate && (
                        <div className="metadata-item">
                          <span className="metadata-label">Fecha del evento:</span>
                          <span className="metadata-value">{formatDate(details.imageData.eventDate)}</span>
                        </div>
                      )}
                    </div>

                    {details.downloadVersions && details.downloadVersions.length > 0 && (
                      <div className="download-versions">
                        <h4>Versiones disponibles para descarga:</h4>
                        {details.downloadVersions.some((v) => v.fileExists === false) && (
                          <p className="download-unavailable-notice">
                            Algunos archivos de alta calidad no están disponibles en el servidor.
                          </p>
                        )}
                        <div className="download-buttons">
                          {details.downloadVersions
                            .filter((v) => v.gn_Path)
                            .map((version, index) => (
                              <button
                                key={version.gn_Id}
                                className={`Button Button--download${
                                  version.fileExists === false ? " Button--download-unavailable" : ""
                                }`}
                                onClick={() => version.fileExists !== false && handleDownload(version.gn_Id)}
                                disabled={version.fileExists === false}
                                title={version.fileExists === false ? "Archivo no disponible en el servidor" : undefined}
                              >
                                {version.fileExists === false
                                  ? `Versión ${index + 1} — No disponible`
                                  : `Descargar versión ${index + 1}`}
                                {version.gn_DataSize &&
                                  ` (${(version.gn_DataSize / 1024 / 1024).toFixed(2)} MB)`}
                              </button>
                            ))}
                          {details.downloadVersions
                            .filter((v) => v.hasInlineData && !v.gn_Path)
                            .map((version, index) => (
                              <button
                                key={version.gn_Id}
                                className="Button Button--download Button--download-low"
                                onClick={() => handleDownload(version.gn_Id)}
                              >
                                Baja calidad {index + 1}
                                {version.gn_DataSize &&
                                  ` (${(version.gn_DataSize / 1024).toFixed(0)} KB)`}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {details.type === "story" && (
                <div className="detail-story">
                  <div className="detail-metadata">
                    <div className="metadata-item metadata-item--full">
                      <span className="metadata-label">Keywords:</span>
                      <span className="metadata-value">
                        {details.keywords || "No hay keywords registradas para este elemento."}
                      </span>
                    </div>
                    {details.storyData?.wordCount && (
                      <div className="metadata-item">
                        <span className="metadata-label">Palabras:</span>
                        <span className="metadata-value">{details.storyData.wordCount.toLocaleString()}</span>
                      </div>
                    )}

                    {details.summary && (
                      <div className="metadata-item metadata-item--full">
                        <span className="metadata-label">Resumen:</span>
                        <span className="metadata-value">{details.summary}</span>
                      </div>
                    )}

                    {details.storyData?.xmlText && (
                      <div className="metadata-item metadata-item--full">
                        <span className="metadata-label">Contenido:</span>
                        <div className="story-content">
                          {formatXmlText(details.storyData.xmlText)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {details.relatedDocuments && details.relatedDocuments.length > 0 && (
                <div className="related-documents">
                  <h3>Página del periódico (PDF):</h3>
                  {details.relatedDocuments.some((doc) => doc.fileExists === false) && (
                    <p className="download-unavailable-notice">
                      La página del periódico no está disponible en el servidor.
                    </p>
                  )}
                  <div className="download-buttons">
                    {details.relatedDocuments.map((doc) => (
                      <button
                        key={doc.id}
                        className={`Button Button--download${
                          doc.fileExists === false ? " Button--download-unavailable" : ""
                        }`}
                        onClick={() => doc.fileExists !== false && handleDownload(doc.dataId)}
                        disabled={doc.fileExists === false}
                        title={doc.fileExists === false ? "Archivo no disponible en el servidor" : undefined}
                      >
                        {doc.title || `Documento ${doc.id}`}
                        {doc.fileExists === false ? " — No disponible" : ""}
                        {doc.sizeBytes ? ` (${(doc.sizeBytes / 1024 / 1024).toFixed(2)} MB)` : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-common">
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <span className="metadata-label">ID:</span>
                    <span className="metadata-value">{details.id}</span>
                  </div>

                   <div className="metadata-item">
                      <span className="metadata-label">Fecha de subida:</span>
                      <span className="metadata-value">{formatDate(details.uploadDate || details.sentDate)}</span>
                    </div>

                    {details.captureDate && (
                      <div className="metadata-item">
                        <span className="metadata-label">Fecha de captura:</span>
                        <span className="metadata-value">{formatDate(details.captureDate)}</span>
                      </div>
                    )}

                    {details.provenance?.name && (
                      <div className="metadata-item">
                        <span className="metadata-label">Procedencia:</span>
                        <span className="metadata-value">{details.provenance.name}</span>
                      </div>
                    )}

                  {details.folderRef && (
                    <div className="metadata-item">
                      <span className="metadata-label">Carpeta:</span>
                      <span className="metadata-value">{details.folderRef}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="modal-error">
              <p>No se pudieron cargar los detalles</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="Button Button--secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
